import React from 'react'
import TiptapEditor from './TiptapEditor'
import type { DeltaJson } from 'lib/value'
import AiMediaPreviewModal, {
    type AiMediaPreviewTarget,
} from 'components/shared/AiMediaPreviewModal'

export type PromptReferenceMention = {
    id: string
    label: string
    kind: 'image' | 'video' | 'audio'
    previewUrl?: string | null
    meta?: string | null
    url?: string | null
    contentType?: string | null
}

type PromptReferenceMentionItem = {
    id: string
    content: string
    value: string
    type: 'reference'
    denotationChar: '@'
    link: string
    meta?: string | null
    kind: PromptReferenceMention['kind']
    previewUrl?: string | null
    url?: string | null
    contentType?: string | null
    isNoResults?: boolean
}

interface PromptEditorProps {
    value: string
    placeholder?: string
    references?: PromptReferenceMention[]
    onValidityChange?: (hasText: boolean) => void
    onTextChange?: (text: string) => void
    onModEnter?: () => void
    // TiptapEditor exposes a Quill-compatible object here; keep this loose for callers.
    onEditorLoad?: (editor: any) => void
    type?: string
    editorTestId?: string
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const EMPTY_REFERENCES: PromptReferenceMention[] = []

function promptTextToDelta(text: string, references: PromptReferenceMention[]): DeltaJson {
    if (!text || references.length === 0) return { ops: [{ insert: text || '' }] }

    const referencesByLabel = new Map(
        references.map(reference => [reference.label.toLowerCase(), reference]),
    )
    const labelPattern = references
        .map(reference => reference.label)
        .sort((left, right) => right.length - left.length)
        .map(escapeRegExp)
        .join('|')
    if (!labelPattern) return { ops: [{ insert: text }] }

    const matcher = new RegExp(`(^|[^\\w])@(${labelPattern})\\b`, 'gi')
    const ops: DeltaJson['ops'] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = matcher.exec(text)) !== null) {
        const prefix = match[1] || ''
        const label = match[2] || ''
        const tokenStart = match.index + prefix.length
        const reference = referencesByLabel.get(label.toLowerCase())
        if (!reference) continue

        if (tokenStart > lastIndex) {
            ops.push({ insert: text.slice(lastIndex, tokenStart) })
        }
        ops.push({ insert: { mention: referenceToMentionItem(reference) } })
        lastIndex = tokenStart + label.length + 1
    }

    if (lastIndex < text.length) ops.push({ insert: text.slice(lastIndex) })

    return { ops: ops.length ? ops : [{ insert: text }] }
}

function promptEditorDeltaToText(delta: DeltaJson | null | undefined): string {
    return (delta?.ops || [])
        .map(op => {
            const insert = op?.insert
            if (typeof insert === 'string') return insert
            if (typeof insert === 'object' && insert && 'mention' in insert) {
                const mention = insert.mention as {
                    content?: string
                    value?: string
                    denotationChar?: string
                }
                const denotationChar = mention.denotationChar || '@'
                const content = mention.content || mention.value || ''
                return content ? `${denotationChar}${content.replace(/^@+/, '')}` : ''
            }
            return ''
        })
        .join('')
}

function referenceToMentionItem(reference: PromptReferenceMention): PromptReferenceMentionItem {
    return {
        id: reference.id,
        content: reference.label,
        value: `@${reference.label}`,
        type: 'reference',
        denotationChar: '@',
        link: reference.url || reference.previewUrl || `#${reference.label}`,
        meta: reference.meta,
        kind: reference.kind,
        previewUrl: reference.previewUrl,
        url: reference.url,
        contentType: reference.contentType,
    }
}

export default function PromptEditor({
    value,
    placeholder,
    references = [],
    onValidityChange,
    onTextChange,
    onModEnter,
    onEditorLoad,
    type = 'answer',
    editorTestId = 'ai-prompt-input-editor',
}: PromptEditorProps) {
    const [previewTarget, setPreviewTarget] = React.useState<AiMediaPreviewTarget | null>(null)
    const editorRef = React.useRef<any>(null)
    const editorTextRef = React.useRef(value)
    const hasTextRef = React.useRef(!!value.trim())
    const referencesRef = React.useRef<PromptReferenceMention[]>(references)
    const onValidityChangeRef = React.useRef(onValidityChange)
    const onTextChangeRef = React.useRef(onTextChange)
    const onModEnterRef = React.useRef(onModEnter)
    const onEditorLoadRef = React.useRef(onEditorLoad)

    React.useEffect(() => {
        onValidityChangeRef.current = onValidityChange
    }, [onValidityChange])

    React.useEffect(() => {
        onTextChangeRef.current = onTextChange
    }, [onTextChange])

    React.useEffect(() => {
        onModEnterRef.current = onModEnter
    }, [onModEnter])

    React.useEffect(() => {
        onEditorLoadRef.current = onEditorLoad
    }, [onEditorLoad])

    const effectiveReferences = references.length > 0 ? references : EMPTY_REFERENCES
    React.useEffect(() => {
        referencesRef.current = effectiveReferences
    }, [effectiveReferences])

    const notifyValidityChange = React.useCallback((nextText: string) => {
        const nextHasText = !!nextText.trim()
        if (nextHasText === hasTextRef.current) return

        hasTextRef.current = nextHasText
        onValidityChangeRef.current?.(nextHasText)
    }, [])

    const initialDefaultValueRef = React.useRef<DeltaJson | null>(null)
    if (!initialDefaultValueRef.current) {
        initialDefaultValueRef.current = promptTextToDelta(value, effectiveReferences)
    }
    const getPromptText = React.useCallback(() => {
        const contents = editorRef.current?.getContents?.()
        if (contents) {
            const nextText = promptEditorDeltaToText(contents)
            editorTextRef.current = nextText
            return nextText
        }

        return editorTextRef.current
    }, [])
    const setPromptText = React.useCallback(
        (nextText: string) => {
            const nextValue = promptTextToDelta(nextText, referencesRef.current)
            const editor = editorRef.current
            if (editor?.setContents) {
                editor.setContents(nextValue, 'api')
            } else {
                initialDefaultValueRef.current = nextValue
            }
            editorTextRef.current = nextText
            notifyValidityChange(nextText)
        },
        [notifyValidityChange],
    )
    const handleTextChange = React.useCallback(
        (delta: DeltaJson) => {
            const nextText = promptEditorDeltaToText(delta)
            editorTextRef.current = nextText
            notifyValidityChange(nextText)
            onTextChangeRef.current?.(nextText)
        },
        [notifyValidityChange],
    )
    const handleEditorLoad = React.useCallback(
        (editor: any) => {
            editorRef.current = editor
            onEditorLoadRef.current?.({
                ...editor,
                getText: getPromptText,
                getValue: getPromptText,
                setText: setPromptText,
                setValue: setPromptText,
                hasText: () => !!getPromptText().trim(),
            })
        },
        [getPromptText, setPromptText],
    )

    React.useEffect(() => {
        if (value === editorTextRef.current) return

        setPromptText(value)
    }, [value, setPromptText])

    React.useEffect(() => {
        return () => {
            onEditorLoadRef.current?.(null)
        }
    }, [])
    const referenceMentionItems = React.useMemo(() => {
        if (effectiveReferences.length > 0) return effectiveReferences.map(referenceToMentionItem)

        return [
            {
                id: 'prompt-reference-no-results',
                content: 'No references available',
                value: '',
                type: 'reference' as const,
                denotationChar: '@' as const,
                link: '#no-references',
                kind: 'image' as const,
                isNoResults: true,
            },
        ]
    }, [effectiveReferences])
    const referencesById = React.useMemo(() => {
        const map = new Map<string, PromptReferenceMention>()
        effectiveReferences.forEach(reference => {
            map.set(reference.id, reference)
        })
        return map
    }, [effectiveReferences])
    const handleMentionClick = React.useCallback(
        (mentionId: string, event: Event) => {
            const reference = referencesById.get(mentionId)
            const url = reference?.url || reference?.previewUrl
            if (!reference || !url) return

            event.preventDefault()
            event.stopPropagation()
            setPreviewTarget({
                kind: reference.kind,
                input: {
                    url,
                    previewImageUrl: reference.previewUrl || null,
                    label: `@${reference.label}`,
                    contentType: reference.contentType || null,
                },
            })
        },
        [referencesById],
    )
    const handleKeyDown = React.useCallback((_editor: unknown, event: KeyboardEvent) => {
        const currentOnModEnter = onModEnterRef.current
        if (!currentOnModEnter || event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) {
            return undefined
        }

        event.preventDefault()
        event.stopPropagation()
        currentOnModEnter()
        return false
    }, [])

    return (
        <>
            <TiptapEditor
                defaultValue={initialDefaultValueRef.current}
                placeholder={placeholder}
                users={referenceMentionItems}
                models={[]}
                actionCommandItems={[]}
                onTextChange={handleTextChange}
                onKeyDown={handleKeyDown}
                onEditorLoad={handleEditorLoad}
                onMentionClick={handleMentionClick}
                type={type}
                data-testid={editorTestId}
            />
            <AiMediaPreviewModal
                target={previewTarget}
                footerMode="done"
                onClose={() => setPreviewTarget(null)}
            />
        </>
    )
}
